import { ReactNode, useEffect, useRef } from "react";
import styled from "styled-components";

/** Marks the body while a page-level action bar is mounted. */
const BODY_CLASS = "page-has-sticky-footer";
const HEIGHT_VAR = "--page-sticky-footer-height";

/**
 * A page's action bar, pinned to the bottom of the viewport while the page
 * scrolls behind it. Render it as the LAST child of the page: `position: sticky`
 * only pins inside its own containing block, so a bar nested in a card would
 * scroll away with the card.
 *
 * It bleeds through the content container's horizontal gutters so the bar spans
 * the full content width, but deliberately not through the fixed sidebar
 * clearance — the bar starts where the content does. The negative bottom margin
 * cancels the container's own bottom padding so the bar sits flush.
 */
const Bar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;

  margin-left: calc(-1 * var(--content-gutter));
  margin-right: calc(-1 * var(--content-gutter));
  margin-bottom: calc(-1 * var(--content-gap-bottom));
  padding: 1rem var(--content-gutter);

  background: var(--brand-white);
  border-top: 1px solid var(--brand-gray-200);

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  .footer-hint {
    color: var(--brand-gray-600);
  }
`;

/**
 * Room above the bar for the lifted "Powered by" badge: the same space the
 * content container keeps below content for it (the badge's height, its offset
 * from the edge, and that gap again above it).
 */
const BadgeClearance = styled.div`
  height: var(--content-gap-bottom);
  flex-shrink: 0;
`;

export function PageActionBar({
  children,
  className,
  "data-testid": testId = "pageActionsFooter",
  reserveBadgeClearance = false,
}: {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
  /**
   * Set when the bar does NOT hold the page's last actions. The bar cancels the
   * container's bottom padding and the badge lifts to sit just above the bar —
   * exactly where the page's last content ends up when scrolled to the bottom
   * (on the account edit page, "Update Hospital"). This restores that room above
   * the bar. Pages whose bar holds the actions (Create Order) leave it off.
   */
  reserveBadgeClearance?: boolean;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  // The floating "Powered by" badge is fixed to the viewport bottom and would
  // otherwise sit on top of these buttons. Publishing the bar's height lets the
  // badge lift clear of it; the bar can grow (a wrapped message, a validation
  // line), so it is measured rather than assumed.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) {
      return undefined;
    }

    document.body.classList.add(BODY_CLASS);

    const publishHeight = () => {
      document.body.style.setProperty(
        HEIGHT_VAR,
        `${bar.getBoundingClientRect().height}px`
      );
    };

    publishHeight();

    // jsdom has no ResizeObserver; the initial measurement is enough there.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publishHeight);
    observer?.observe(bar);

    return () => {
      observer?.disconnect();
      document.body.classList.remove(BODY_CLASS);
      document.body.style.removeProperty(HEIGHT_VAR);
    };
  }, []);

  return (
    <>
      {reserveBadgeClearance && (
        <BadgeClearance aria-hidden data-testid={`${testId}.badgeClearance`} />
      )}
      <Bar ref={barRef} className={className} data-testid={testId}>
        {children}
      </Bar>
    </>
  );
}
